import { useState, useEffect } from 'react';
import { D, st } from '../constants';

const empGet = (token, path) =>
  fetch(`/api${path}`, {headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}}).then(r=>r.json());
const empPost = (token, path, body={}) =>
  fetch(`/api${path}`, {method:'POST',headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'},body:JSON.stringify(body)}).then(r=>r.json());

export default function EmployeeApp({ identity }) {
  const [view, setView] = useState('pin');
  const [pin, setPin] = useState('');
  const [empToken, setEmpToken] = useState(null);
  const [err, setErr] = useState('');
  const [logging, setLogging] = useState(false);
  const [logJob, setLogJob] = useState(null);
  const [logForm, setLogForm] = useState(null);
  const [logSaving, setLogSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const loadStatus = async (token) => {
    const [s, j] = await Promise.all([
      empGet(token, '/employee/clock/status'),
      empGet(token, '/employee/jobs'),
    ]);
    setStatus(s);
    setJobs(Array.isArray(j) ? j : []);
  };

  const handleLogin = async () => {
    if (pin.length !== 4) return;
    setLogging(true); setErr('');
    try {
      const result = await fetch('/api/employee/login', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ userId: identity.userId, companyId: identity.companyId, pin }),
      }).then(r => r.json());
      if (result.error) { setErr('Incorrect PIN'); setLogging(false); return; }
      localStorage.setItem('eb:employee', JSON.stringify({...identity, token: result.token}));
      setEmpToken(result.token);
      await loadStatus(result.token);
      setView('clock');
    } catch(e) { setErr('Connection error. Try again.'); }
    setLogging(false);
  };

  const dayIn  = async () => { setLoading(true); await empPost(empToken, '/employee/clock/day-in');  await loadStatus(empToken); setLoading(false); };
  const dayOut = async () => {
    if (!confirm('Clock out for the day?')) return;
    setLoading(true); await empPost(empToken, '/employee/clock/day-out'); await loadStatus(empToken); setLoading(false);
  };
  const jobIn  = async (jobId) => { setLoading(true); await empPost(empToken, '/employee/clock/job-in', {jobId}); await loadStatus(empToken); setLoading(false); };
  const jobOut = async () => { setLoading(true); await empPost(empToken, '/employee/clock/job-out'); await loadStatus(empToken); setLoading(false); };

  const elapsed = (startedAt) => {
    const ms = now - new Date(startedAt);
    const h = Math.floor(ms/3600000), m = Math.floor((ms%3600000)/60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const logout = () => { localStorage.removeItem('eb:employee'); window.location.href = '/'; };
  const openLogForm = (j) => {
    const hrs = (status && status.todayJobs ? status.todayJobs : []).find(function(tj){return tj.job_id===j.id;});
    setLogJob(j);
    setLogForm({date:new Date().toISOString().slice(0,10),hours:hrs?String(hrs.hours):'',description:'',materials:''});
  };
  const submitLog = async () => {
    if(!logForm.description&&!Number(logForm.hours)){alert('Please enter hours or a description.');return;}
    setLogSaving(true);
    try{
      await empPost(empToken,'/employee/log/'+logJob.id,logForm);
      setLogJob(null);setLogForm(null);
    }catch(e){alert('Failed to save: '+(e.message||e));}
    setLogSaving(false);
  };

  if (view === 'pin') return (
    <div style={{minHeight:"100vh",background:D.surface,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:36,fontWeight:800,color:D.accent}}>⚡ AmpBooks</div>
        </div>
        <div style={st.card}>
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:D.accent+"22",border:`2px solid ${D.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:700,color:D.accent,margin:"0 auto 12px"}}>
              {(identity.name||"?").charAt(0).toUpperCase()}
            </div>
            <div style={{fontWeight:700,fontSize:20,marginBottom:4}}>Welcome back, {identity.name}!</div>
            <div style={{color:D.muted,fontSize:13}}>Enter your PIN to clock in</div>
          </div>
          <input style={{...st.input,fontSize:28,textAlign:"center",letterSpacing:14,marginBottom:16}}
            type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4}
            value={pin} autoFocus placeholder="••••"
            onChange={e => setPin(e.target.value.replace(/\D/g,'').slice(0,4))}
            onKeyDown={e => e.key==='Enter' && handleLogin()}/>
          {err && <div style={{color:D.danger,fontSize:13,textAlign:"center",marginBottom:12}}>{err}</div>}
          <button style={{...st.btn("primary"),width:"100%",marginLeft:0,padding:"14px 0",fontSize:16}}
            onClick={handleLogin} disabled={logging || pin.length!==4}>
            {logging ? "Checking…" : "Enter →"}
          </button>
          <div style={{textAlign:"center",marginTop:16}}>
            <button style={{background:"none",border:"none",color:D.muted,fontSize:12,cursor:"pointer"}} onClick={logout}>
              Not {identity.name}? Switch account
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const daySession = status?.daySession;
  const jobSession = status?.jobSession;
  const todayJobs  = status?.todayJobs || [];
  const totalJobHours = todayJobs.reduce((s,j) => s+(Number(j.hours)||0), 0);

  return (
    <div style={{minHeight:"100vh",background:D.surface}}>
      <div style={{background:D.card,borderBottom:`1px solid ${D.border}`,padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontWeight:800,fontSize:18,color:D.accent}}>⚡ AmpBooks</div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:13,color:D.muted}}>{identity.name}</div>
          <button style={{background:"none",border:`1px solid ${D.border}`,borderRadius:6,padding:"4px 10px",color:D.muted,fontSize:11,cursor:"pointer"}} onClick={logout}>Sign Out</button>
        </div>
      </div>

      <div style={{padding:20,maxWidth:500,margin:"0 auto"}}>

        {/* Day clock */}
        <div style={{...st.card,marginBottom:16,border:`2px solid ${daySession?D.success:D.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:15}}>Workday</div>
            {daySession && <div style={{fontSize:12,color:D.success,fontWeight:600}}>● CLOCKED IN</div>}
          </div>
          {daySession ? (
            <div>
              <div style={{textAlign:"center",marginBottom:16}}>
                <div style={{fontSize:11,color:D.muted,marginBottom:4}}>Time at work</div>
                <div style={{fontSize:42,fontWeight:800,color:D.success,lineHeight:1}}>{elapsed(daySession.started_at)}</div>
                <div style={{fontSize:11,color:D.muted,marginTop:4}}>Since {new Date(daySession.started_at).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>
              </div>
              <button style={{...st.btn("danger"),width:"100%",marginLeft:0,padding:"13px 0",fontSize:14,opacity:jobSession?0.5:1}}
                onClick={dayOut} disabled={loading||!!jobSession}>
                {jobSession ? "Clock out of job first" : "⏹ Clock Out for the Day"}
              </button>
            </div>
          ) : (
            <div>
              <div style={{textAlign:"center",marginBottom:16,fontSize:20,fontWeight:300,color:D.muted}}>
                {now.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",hour12:true})}
              </div>
              <button style={{background:D.success,color:"#fff",border:"none",borderRadius:9,cursor:"pointer",width:"100%",padding:"14px 0",fontSize:16,fontWeight:700}}
                onClick={dayIn} disabled={loading}>▶ Clock In for the Day</button>
            </div>
          )}
        </div>

        {/* Job clock — only when clocked in for day */}
        {daySession && (
          <div style={{...st.card,marginBottom:16,border:`2px solid ${jobSession?D.accent:D.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontWeight:700,fontSize:15}}>Current Job</div>
              {jobSession && <div style={{fontSize:12,color:D.accent,fontWeight:600}}>● ON JOB</div>}
            </div>
            {jobSession ? (
              <div>
                <div style={{textAlign:"center",marginBottom:16}}>
                  <div style={{fontWeight:600,fontSize:17,marginBottom:6,color:D.text}}>{jobSession.job_title}</div>
                  <div style={{fontSize:36,fontWeight:800,color:D.accent,lineHeight:1}}>{elapsed(jobSession.started_at)}</div>
                  <div style={{fontSize:11,color:D.muted,marginTop:4}}>Since {new Date(jobSession.started_at).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>
                </div>
                <button style={{...st.btn("danger"),width:"100%",marginLeft:0,padding:"13px 0",fontSize:14}}
                  onClick={jobOut} disabled={loading}>⏹ Clock Out of Job</button>
              </div>
            ) : (
              <div>
                <div style={{fontSize:12,color:D.muted,marginBottom:12}}>Select a job to clock into:</div>
                {jobs.length===0 ? (
                  <div style={{textAlign:"center",color:D.muted,fontSize:13,padding:"20px 0"}}>No active jobs right now</div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {jobs.map(j => (
                      <div key={j.id} style={{background:D.surface,border:`1px solid ${D.border}`,borderRadius:9,padding:"13px 14px",display:"flex",flexDirection:"column",gap:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div>
                            <div style={{fontWeight:600,fontSize:14,color:D.text}}>{j.title}</div>
                            {j.address && <div style={{fontSize:11,color:D.muted,marginTop:2}}>{j.address}</div>}
                          </div>
                          <div style={{fontSize:11,background:j.status==="In Progress"?D.success+"22":D.warning+"22",color:j.status==="In Progress"?D.success:D.warning,padding:"3px 9px",borderRadius:5,fontWeight:600,flexShrink:0}}>{j.status}</div>
                        </div>
                        <div style={{display:"flex",gap:6}}>
                          <button onClick={()=>jobIn(j.id)} disabled={loading} style={{...st.btn("primary"),flex:1,marginLeft:0,padding:"8px 0",fontSize:13}}>Clock In</button>
                          <button onClick={()=>openLogForm(j)} disabled={loading} style={{...st.btn(),flex:1,marginLeft:0,padding:"8px 0",fontSize:13}}>Log Work</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Today's summary */}
        {todayJobs.length > 0 && (
          <div style={st.card}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>Today's Work</div>
            {todayJobs.map((j,i) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"7px 0",borderBottom:i<todayJobs.length-1?`1px solid ${D.border}`:"none"}}>
                <div style={{color:D.text}}>{j.job_title||"General"}</div>
                <div style={{fontWeight:600,color:D.accent}}>{Number(j.hours||0).toFixed(2)}h</div>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",marginTop:10,paddingTop:10,borderTop:`1px solid ${D.border}`,fontSize:14,fontWeight:700}}>
              <span>Total on jobs</span>
              <span style={{color:D.accent}}>{totalJobHours.toFixed(2)}h</span>
            </div>
          </div>
        )}
        {logJob && logForm && (
          <div style={{position:'fixed',bottom:0,left:0,right:0,background:D.bg,border:`1px solid ${D.border}`,borderTopLeftRadius:16,borderTopRightRadius:16,padding:20,zIndex:100,maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{fontWeight:700,fontSize:15,marginBottom:16}}>Log Work — {logJob.title}</div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Date</div>
                <input type='date' style={st.input} value={logForm.date} onChange={function(e){setLogForm(function(f){return{...f,date:e.target.value};});}}/></div>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Hours worked</div>
                <input type='number' step='0.25' min='0' style={st.input} value={logForm.hours} onChange={function(e){setLogForm(function(f){return{...f,hours:e.target.value};});}} placeholder='0'/></div>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>What was done</div>
                <textarea style={{...st.input,height:72,resize:'none'}} value={logForm.description} onChange={function(e){setLogForm(function(f){return{...f,description:e.target.value};});}} placeholder='Describe the work completed today'/></div>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Materials used</div>
                <textarea style={{...st.input,height:56,resize:'none'}} value={logForm.materials} onChange={function(e){setLogForm(function(f){return{...f,materials:e.target.value};});}} placeholder='6 single gang boxes, 120 of 12/2, box of staples'/></div>
            </div>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button style={{...st.btn(),flex:1,marginLeft:0}} onClick={function(){setLogJob(null);setLogForm(null);}}>Cancel</button>
              <button style={{...st.btn('primary'),flex:1,marginLeft:0}} disabled={logSaving} onClick={submitLog}>{logSaving?'Saving...':'Save Log'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
